import React from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import styledContainerQuery from 'styled-container-query';

import { ApplicableProduct, GamificationThresholdType } from 'src/types';
import CartItemClass from 'src/state/cart-item';
import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import { formatCurrency, pluralize } from 'shared/helpers/utils';
import { PlusIcon } from 'src/assets/plus';
import usePaths from 'src/hooks/use-paths';
import { Progress } from './progress';
import { MatchingItem } from './matching-item';

type ThresholdType = Exclude<GamificationThresholdType, 'PRODUCT_GRAMS'>;

type OfferProps = {
  specialId?: string;
  specialName?: string;
  thresholdType: ThresholdType;
  threshold: number;
  currentAmount: number;
  cartItemIds: ApplicableProduct[];
  isCompleted?: boolean;
  shouldDisplayItems?: boolean;
  shouldLinkToSpecialOfferPage?: boolean;
  shouldDisplaySpecialHeading?: boolean;
  shouldAlwaysUseSpecialNameAsTitle?: boolean;
};

export const Offer = ({
  specialId,
  specialName,
  thresholdType,
  threshold,
  currentAmount,
  cartItemIds,
  isCompleted = false,
  shouldDisplayItems = false,
  shouldDisplaySpecialHeading = false,
  shouldLinkToSpecialOfferPage = false,
  shouldAlwaysUseSpecialNameAsTitle = false,
}: OfferProps): JSX.Element => {
  const numberOfItems = thresholdType === 'NUMBER_OF_ITEMS' ? threshold : 0;
  const shouldItemsAlwaysBeSeparateRow = numberOfItems > 8;
  const shouldItemsBeSeparateRowMiddleBreakpoint = numberOfItems > 6;
  const { closeCartDrawer, closeCartInterstitial, openCartDrawer } = useUI();
  const { items } = useCart();
  const { href: specialOfferHref } = usePaths({ specials: true, id: `offer/${String(specialId)}` });
  const displayItemSlots = shouldDisplayItems && thresholdType === 'NUMBER_OF_ITEMS' && numberOfItems <= 10;
  const progress = Math.max(0, (currentAmount / threshold) * 100);

  // Create an array that includes each item based on its amount
  const matchingItems =
    displayItemSlots && cartItemIds.length
      ? (cartItemIds.flatMap((cartItemId) => {
          const item = Object.values(items).find((cartItem: CartItemClass) => cartItem.key === cartItemId.productKey);
          return item ? Array(cartItemId.amount).fill(item) : [];
        }) as CartItemClass[])
      : [];

  const viewCart = (): void => {
    closeCartInterstitial();
    openCartDrawer();
  };

  const { title, description, shouldDisplayViewEligibleItemsLink } = getContent({
    thresholdType,
    threshold,
    currentAmount,
    isCompleted,
    shouldDisplaySpecialHeading,
    title: shouldAlwaysUseSpecialNameAsTitle ? specialName : undefined,
    viewCart,
  });

  return (
    <Wrapper
      $shouldItemsAlwaysBeSeparateRow={shouldItemsAlwaysBeSeparateRow}
      $numberOfItems={numberOfItems}
      $shouldItemsBeSeparateRowMiddleBreakpoint={shouldItemsBeSeparateRowMiddleBreakpoint}
      data-testid={`offer-${String(specialId)}`}
    >
      {shouldDisplaySpecialHeading && specialName && <Heading data-testid='offer-heading'>{specialName}</Heading>}

      <Header $shouldItemsAlwaysBeSeparateRow={shouldItemsAlwaysBeSeparateRow}>
        <Progress progress={Number.isFinite(progress) ? progress : 0} isCompleted={isCompleted} />

        <div>
          <Title data-testid='offer-title' as={shouldDisplaySpecialHeading && specialName ? 'h3' : 'h2'}>
            {title}
          </Title>
          <Description data-testid='offer-description'>{description}</Description>

          {(shouldLinkToSpecialOfferPage || shouldDisplayViewEligibleItemsLink) && (
            <Link href={specialOfferHref}>
              <Anchor href={specialOfferHref} onClick={closeCartDrawer}>
                View Eligible Items
              </Anchor>
            </Link>
          )}
        </div>
      </Header>

      {displayItemSlots && (
        <Items
          data-testid='offer-items'
          $shouldItemsAlwaysBeSeparateRow={shouldItemsAlwaysBeSeparateRow}
          $numberOfItems={numberOfItems}
        >
          {matchingItems.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={`${String(item.key)}-${index}`} data-testid='offer-item-matching'>
              <MatchingItem item={item} />
            </Item>
          ))}

          {Array.from({ length: threshold - matchingItems.length }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Item key={`placeholder-${index}`} data-testid='offer-item-placeholder'>
              <PlusIcon height={14} width={14} />
            </Item>
          ))}
        </Items>
      )}
    </Wrapper>
  );
};

type GetContentProps = {
  thresholdType: ThresholdType;
  threshold: number;
  currentAmount: number;
  isCompleted: boolean;
  shouldDisplaySpecialHeading: boolean;
  title?: string;
  viewCart: () => void;
};

type GetContentReturn = {
  title?: string;
  description: React.ReactNode;
  shouldDisplayViewEligibleItemsLink?: boolean;
};

const getContent = ({
  thresholdType,
  threshold,
  currentAmount,
  isCompleted,
  shouldDisplaySpecialHeading,
  title,
  viewCart,
}: GetContentProps): GetContentReturn => {
  const remainingAmount = threshold - currentAmount;
  const isNumberOfItemsThreshold = thresholdType === 'NUMBER_OF_ITEMS';

  if (isCompleted) {
    return {
      title: title ?? 'You got the offer!',
      description: (
        <>
          View your savings <ViewCartButton onClick={viewCart}>in the cart.</ViewCartButton>
        </>
      ),
    };
  }

  if (currentAmount === 0) {
    return {
      title: title ?? 'Get the offer!',
      description: isNumberOfItemsThreshold
        ? `Add ${remainingAmount} eligible ${String(pluralize('item', remainingAmount))} to complete the offer.`
        : `Add ${formatCurrency(remainingAmount, {
            trimZeroCents: true,
          })} of eligible items to complete the offer`,
    };
  }

  if (isNumberOfItemsThreshold) {
    return {
      title:
        title ??
        (!shouldDisplaySpecialHeading
          ? `${currentAmount} eligible ${String(pluralize('item', currentAmount))} in your cart`
          : undefined),
      description: `Add ${remainingAmount} more to complete the offer.`,
      shouldDisplayViewEligibleItemsLink: shouldDisplaySpecialHeading,
    };
  }

  return {
    title: title ?? (!shouldDisplaySpecialHeading ? 'Complete the offer!' : undefined),
    description: `Add ${formatCurrency(remainingAmount, {
      trimZeroCents: true,
    })} more to complete the offer.`,
    shouldDisplayViewEligibleItemsLink: shouldDisplaySpecialHeading,
  };
};

const getNumberOfColumns = (numberOfItems: number): number => {
  if (numberOfItems <= 4) {
    return numberOfItems;
  }
  if (numberOfItems <= 6) {
    return 3;
  }
  if (numberOfItems <= 8) {
    return 4;
  }
  return 5;
};

const Header = styled.div`
  display: flex;
  gap: 12px;

  ${({ $shouldItemsAlwaysBeSeparateRow }) =>
    !$shouldItemsAlwaysBeSeparateRow &&
    css`
      min-width: 270px;
    `}
`;

const Items = styled.ul`
  display: grid;
  gap: 8px;
  list-style: none;
  justify-content: start;
  margin: 0;
  padding: 0;

  ${({ $numberOfItems }) => css`
    grid-template-columns: repeat(${$numberOfItems}, minmax(56px, 160px));
  `}

  ${({ $shouldItemsAlwaysBeSeparateRow }) =>
    !$shouldItemsAlwaysBeSeparateRow
      ? css`
          max-width: 565px;
        `
      : css`
          width: 100%;
        `}
`;

const Item = styled.li`
  align-items: center;
  background: #fff;
  border-radius: ${({ theme }) => theme.customized.radius.md};
  color: #d2d7da;
  display: flex;
  justify-content: center;
  height: 56px;
`;

const Wrapper = styledContainerQuery.div`
  background: #ceeedb;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 16px;
  width: 100%;

  ${({ $shouldItemsAlwaysBeSeparateRow }) =>
    $shouldItemsAlwaysBeSeparateRow
      ? css`
          flex-direction: column;
        `
      : css`
          align-items: center;
        `}

  &:container(max-width: 856px) {
    ${({ $shouldItemsBeSeparateRowMiddleBreakpoint }) =>
      $shouldItemsBeSeparateRowMiddleBreakpoint &&
      css`
        align-items: flex-start;
        flex-direction: column;

        ${Header} {
          min-width: auto;
          order: 99999; // This is the last item in this flexed container at this breakpoint
        }

        ${Items} {
          max-width: none;
          width: 100%;
        }
      `}
  }

  // Items will always be on separate row at this breakpoint
  &:container(max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;

    ${Header} {
      min-width: auto;
      order: 99999; // This is the last item in this flexed container at this breakpoint
    }

    ${Items} {
      width: 100%;

      ${({ $numberOfItems }) => css`
        grid-template-columns: repeat(${getNumberOfColumns($numberOfItems)}, minmax(56px, 160px));
      `}
      }}
    }
  }
`;

const Heading = styled.h2`
  font-size: 16px;
  font-weight: 600;
  line-height: ${20 / 16};
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  line-height: ${18 / 14};
`;

const Description = styled.p`
  font-size: 14px;
  line-height: ${18 / 14};
  margin-top: 4px;
`;

const ViewCartButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: inherit;
  padding: 0;
  text-align: left;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const Anchor = styled.a`
  color: #00624a;
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: ${16 / 13};
  margin-top: 4px;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;
